import { type ChangeEvent, type RefObject, useContext, useRef } from "react";

import { FileContext } from "@styled/file_context";

/**
 * Owns the hidden `<input type="file">` and the click-to-open affordance shared
 * by every upload surface in the Add Records flow. Consumed directly by
 * button-only pickers (e.g. the `Upload & Process` CTA) and composed inside
 * `useFileDropZone` for drop-zone + picker combos.
 *
 * Pass `multiple: true` when the picker should accept multi-file selection.
 * Drag-drop (in `useFileDropZone`) accepts many files regardless; this flag
 * only affects the OS file picker invoked on click.
 */
export function useFilePicker(options: { multiple?: boolean } = {}): {
  inputRef: RefObject<HTMLInputElement | null>;
  openPicker: () => void;
  inputProps: {
    type: "file";
    className: "sr-only";
    multiple: boolean;
    onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  };
} {
  const { onSelect } = useContext(FileContext);
  const inputRef = useRef<HTMLInputElement>(null);
  const multiple = options.multiple ?? false;

  const openPicker = () => inputRef.current?.click();

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) onSelect([...files]);
    event.target.value = "";
  };

  return {
    inputRef,
    openPicker,
    inputProps: {
      type: "file",
      className: "sr-only",
      multiple,
      onChange: handleInputChange,
    },
  };
}
